import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '../components/ui/carousel';
import { Table, TableBody, TableCell, TableRow } from '../components/ui/table';
import { Heart, Shield, ArrowLeft, Bell, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const DEFAULT_WHATSAPP = '971503634567';

function WhatsAppIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [watch, setWatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inWishlist, setInWishlist] = useState(false);

  useEffect(() => {
    fetchWatch();
    if (user) {
      checkWishlist();
      if (searchParams.get('openChat') === '1') {
        handleContactSeller();
      }
    }
  }, [id, user]);

  const fetchWatch = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/listings/${id}`);
      setWatch(res.data);
    } catch (error) {
      toast.error('Watch not found');
      navigate('/catalog');
    } finally {
      setLoading(false);
    }
  };

  const checkWishlist = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/wishlist`);
      const ids = res.data.listings.map(w => w.id);
      setInWishlist(ids.includes(id));
    } catch (e) {}
  };

  const handleWishlistToggle = async () => {
    if (!user) { toast.error('Please login to add to wishlist'); navigate('/login'); return; }
    try {
      const res = await axios.post(`${API_URL}/api/wishlist/toggle`, { listing_id: id });
      setInWishlist(res.data.in_wishlist);
      toast.success(res.data.message);
    } catch (e) { toast.error('Failed to update wishlist'); }
  };

  const handleContactSeller = async () => {
    if (!user) {
      toast.error('Please sign in to contact the seller');
      navigate(`/login?returnTo=/watch/${id}?openChat=1`);
      return;
    }
    try {
      const res = await axios.post(`${API_URL}/api/chat/start`, { listingId: id });
      navigate(`/messages?id=${res.data.conversationId}`);
    } catch (e) { toast.error('Failed to start conversation'); }
  };

  const handleWhatsApp = () => {
    if (!watch) return;
    const watchUrl = window.location.href;
    const price = formatPrice(watch.price, watch.currency);
    const ref = watch.refNumber || 'N/A';
    const message = `Hi V Watch team, I'm interested in ${watch.title} listed at ${price}. Ref: ${ref}. Link: ${watchUrl}. Is it still available? Please share the next steps to proceed.`;
    const encoded = encodeURIComponent(message);
    const phone = DEFAULT_WHATSAPP;
    window.open(`https://wa.me/${phone}?text=${encoded}`, '_blank');
  };

  const formatPrice = (price, currency = 'USD') => new Intl.NumberFormat('en-US', { style: 'currency', currency, minimumFractionDigits: 0 }).format(price);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div></div>;
  }

  if (!watch) return null;

  return (
    <div className="min-h-screen py-8" data-testid="product-detail-page">
      <div className="mx-auto px-4 sm:px-6 lg:px-8 max-w-[1280px]">
        <Button variant="ghost" size="sm" onClick={() => navigate('/catalog')} className="mb-6 text-xs" data-testid="back-to-catalog-button">
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Catalog
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Image Gallery */}
          <div>
            <Carousel className="w-full" data-testid="product-image-gallery">
              <CarouselContent>
                {watch.images && watch.images.length > 0 ? watch.images.map((image, i) => (
                  <CarouselItem key={i}>
                    <div className="aspect-square overflow-hidden rounded-xl bg-black/40 relative">
                      <img src={image} alt={`${watch.title} - Image ${i + 1}`} className="h-full w-full object-cover" />
                      {watch.status === 'sold' && <div className="absolute inset-0 bg-black/40 flex items-center justify-center"><Badge className="bg-red-600 text-white border-0 text-lg px-6 py-2">Sold</Badge></div>}
                    </div>
                  </CarouselItem>
                )) : (
                  <CarouselItem>
                    <div className="aspect-square overflow-hidden rounded-xl bg-black/40">
                      <img src="https://images.unsplash.com/photo-1589688934051-ca9059383f4f?w=800" alt={watch.title} className="h-full w-full object-cover" />
                    </div>
                  </CarouselItem>
                )}
              </CarouselContent>
              {watch.images && watch.images.length > 1 && (<><CarouselPrevious /><CarouselNext /></>)}
            </Carousel>
          </div>

          {/* Product Info */}
          <div>
            <div className="mb-6">
              <div className="flex items-start justify-between mb-2">
                <h1 className="font-display text-2xl sm:text-3xl text-foreground tracking-tight font-semibold" data-testid="product-detail-title">{watch.title}</h1>
                <Button variant="ghost" size="icon" className="flex-shrink-0" onClick={handleWishlistToggle} data-testid="product-wishlist-toggle-button">
                  <Heart className={`h-5 w-5 ${inWishlist ? 'fill-primary text-primary' : ''}`} />
                </Button>
              </div>
              {watch.refNumber && <p className="text-sm text-muted-foreground mb-4">Reference: {watch.refNumber}</p>}
              <div className="flex items-center gap-3 mb-6">
                <Badge className="bg-transparent border-primary text-primary" data-testid="product-detail-condition-badge">{watch.condition}</Badge>
                {watch.status === 'sold' && <Badge className="bg-red-600 text-white border-0">Sold</Badge>}
              </div>
              <p className="text-3xl sm:text-4xl font-bold text-primary mb-8" data-testid="product-detail-price-text">{formatPrice(watch.price, watch.currency)}</p>
            </div>

            {/* CTAs */}
            <div className="flex flex-col gap-4 mb-8">
              {watch.status === 'sold' ? (
                <>
                  <div className="bg-red-900/20 border border-red-800/30 rounded-lg p-4 text-center">
                    <p className="text-red-300 text-sm font-medium">This watch is currently sold out</p>
                  </div>
                  <Button size="lg" className="w-full" variant="outline" onClick={() => toast.success('You will be notified when this watch becomes available')} data-testid="product-notify-button">
                    <Bell className="mr-2 h-4 w-4" /> Notify Me When Available
                  </Button>
                </>
              ) : (
                <>
                  <button
                    onClick={handleContactSeller}
                    className="w-full h-12 rounded-md text-sm font-medium bg-card text-foreground border border-border hover:bg-primary hover:text-primary-foreground transition-colors flex items-center justify-center gap-2"
                    data-testid="product-contact-seller-button"
                  >
                    <MessageSquare className="h-4 w-4" /> Contact Seller
                  </button>
                  <button
                    onClick={handleWhatsApp}
                    className="w-full h-12 rounded-md text-sm font-medium bg-transparent text-emerald-400 border border-emerald-700/50 hover:bg-emerald-900/20 hover:text-emerald-300 transition-colors flex items-center justify-center gap-2"
                    data-testid="product-whatsapp-button"
                  >
                    <WhatsAppIcon className="h-4 w-4" /> Connect to WhatsApp
                  </button>
                </>
              )}
            </div>

            {/* Specifications */}
            <Card className="p-6 border-border bg-card mb-6" data-testid="product-specifications-section">
              <h3 className="font-display text-lg text-foreground mb-4 font-semibold">Specifications</h3>
              <Table>
                <TableBody>
                  {[
                    ['Brand', watch.brand],
                    ['Model', watch.model],
                    ['Year', watch.year],
                    ['Case Size', watch.caseSize],
                    ['Material', watch.material],
                    ['Movement', watch.movement],
                    ['Dial Color', watch.dialColor],
                    ['Strap/Bracelet', watch.strap],
                    ['Box & Papers', watch.boxPapers],
                    ['Location', watch.location],
                  ].filter(([, v]) => v).map(([label, value]) => (
                    <TableRow key={label}>
                      <TableCell className="font-medium text-sm py-2.5">{label}</TableCell>
                      <TableCell className="text-sm py-2.5" data-testid={`spec-${label.toLowerCase().replace(/\s+/g, '-')}`}>{value}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>

            {/* Description */}
            <Card className="p-6 border-border bg-card mb-6" data-testid="product-description-section">
              <h3 className="font-display text-lg text-foreground mb-4 font-semibold">Description</h3>
              <p className="text-sm text-muted-foreground leading-relaxed" data-testid="product-description-text">{watch.description}</p>
            </Card>

            {/* Authentication */}
            <Card className="p-6 border-border bg-card" data-testid="product-authentication-section">
              <div className="flex items-start gap-4">
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Shield className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground mb-1">V watch Authentication</h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    This timepiece has been authenticated by our team of experts. Every watch sold through V watch includes our authenticity guarantee and secure escrow protection.
                  </p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
